import { type LeadListItem, type LeadPriority } from '@utazzo/shared';
import { UzAvatar, UzBadge } from '@utazzo/ui';
import React from 'react';

import { isCarryforwardLead } from './carryforward';
import { resolveStageTone, stageBadgeTone } from './stage-color';

import { type DataGridColumn } from '@/components/data-grid';
import { type ListScreenColumns } from '@/components/list-screen';

const PRIORITY_TONE: Record<LeadPriority, 'secondary' | 'warning' | 'destructive' | 'default'> = {
  LOW: 'secondary',
  NORMAL: 'default',
  HIGH: 'warning',
  URGENT: 'destructive',
};

export const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const formatBudget = (
  amount: LeadListItem['budgetAmount'],
  currency: LeadListItem['budgetCurrency'],
): string => {
  if (amount === null || amount === undefined) return '-';
  const code = currency ?? 'INR';
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${code} ${amount.toLocaleString('en-IN')}`;
  }
};

// Sub-stage indicator colours. The QuerySubStatus master's own `color` wins;
// these are the fallbacks by code when a sub-status has no color set.
const SUB_STAGE_HEX: Record<string, string> = {
  proposal: '#3b82f6',
  warm: '#f59e0b',
  hot: '#ef4444',
};
const subStageHex = (color: string | null, code: string): string =>
  color ?? SUB_STAGE_HEX[code.toLowerCase()] ?? '#64748b';

/**
 * Single source of truth for the All-Leads grid columns. Shared between the
 * Lead inbox list (LeadsListPage) and the reports drill-down dialog so the two
 * never drift. `isAdmin` appends the 6 ad-attribution columns at the end. Every
 * grid shows Destination, Original owner (the agent a lead was released from)
 * and Current assigned, so reassignment + routing is always clear.
 */
export const useLeadListColumns = (opts: { isAdmin: boolean }): ListScreenColumns<LeadListItem> => {
  const { isAdmin } = opts;
  return React.useMemo(
    () =>
      ({ state }) => {
        const cols: ReadonlyArray<DataGridColumn<LeadListItem>> = [
          {
            id: 'serial',
            header: 'S.No',
            width: '64px',
            cell: (_row, index) => (
              <span className="text-zinc-500">
                {(state.page - 1) * state.pageSize + index + 1}.
              </span>
            ),
          },
          {
            id: 'customer',
            header: 'Customer',
            cell: (row) => (
              <div className="flex min-w-0 items-center gap-2.5">
                <UzAvatar name={row.customer.displayName} size="sm" />
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-sm font-medium text-zinc-900">
                    {row.customer.displayName}
                  </span>
                  <span className="truncate font-mono text-[11px] text-zinc-500">
                    {row.reference}
                  </span>
                </div>
                {/* Carry-forward chip. Driven by the canonical
                    `isCarryforwardLead` helper so the row indicator
                    matches the Carry Forward strip's count exactly
                    (createdAt before this month + bucket=OPEN). The
                    operator's inbox date-range no longer changes
                    which leads get the pill - the rule is a fixed
                    calendar boundary so the visual stays honest
                    regardless of how the operator filters. */}
                {isCarryforwardLead(row) && (
                  <span
                    className="ml-1 inline-flex items-center rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-amber-200"
                    title="This lead was created in a prior month but is still OPEN - it's a carry-forward."
                  >
                    Carry forward
                  </span>
                )}
              </div>
            ),
          },
          {
            id: 'phone',
            header: 'Contact',
            cell: (row) => (
              <span className="font-mono text-xs text-zinc-700">
                {row.customer.mobileNumber ?? row.customer.email ?? '-'}
              </span>
            ),
          },
          // 'title' column intentionally NOT rendered (2026-06-24 ops
          // request). The Lead title is still in the response shape
          // (drives detail page + bookings handoff) - just not in
          // the list grid.
          {
            id: 'createdAt',
            header: 'Created',
            sortable: true,
            cell: (row) => (
              <span className="text-xs whitespace-nowrap text-zinc-700">
                {formatDate(row.createdAt)}
              </span>
            ),
          },
          {
            id: 'pax',
            header: 'Pax',
            cell: (row) => (
              <span className="font-mono text-xs whitespace-nowrap text-zinc-600">
                {row.travellerAdults}A · {row.travellerChildren}C · {row.travellerInfants}I
              </span>
            ),
          },
          {
            id: 'destination',
            header: 'Destination',
            cell: (row) => {
              // Destination master, else the city, else the free-text label the
              // website form captured - same chain the reports use.
              const dest =
                row.destination?.name ??
                row.destinationCity?.name ??
                row.destinationCityText ??
                null;
              return dest ? (
                <span className="truncate text-xs text-zinc-700" title={dest}>
                  {dest}
                </span>
              ) : (
                <span className="text-xs text-zinc-400">-</span>
              );
            },
          },
          {
            id: 'currentStatus',
            header: 'Stage',
            cell: (row) => (
              <UzBadge
                tone={stageBadgeTone(resolveStageTone(row.currentBucket, row.currentStatus.name))}
                pill
              >
                {row.currentStatus.name}
              </UzBadge>
            ),
          },
          {
            id: 'currentSubStatus',
            header: 'Sub-stage',
            // Finer-grained step under the stage (e.g. Proposal / Warm / Hot
            // under Active). Colour comes from the sub-status master, falling
            // back to the per-code hex. Null for stages that don't sub-divide.
            cell: (row) => {
              if (!row.currentSubStatus) return <span className="text-xs text-zinc-400">-</span>;
              const hex = subStageHex(row.currentSubStatus.color, row.currentSubStatus.code);
              return (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium"
                  style={{ backgroundColor: `${hex}1a`, color: hex }}
                >
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: hex }}
                    aria-hidden
                  />
                  {row.currentSubStatus.name}
                </span>
              );
            },
          },
          {
            id: 'lostReason',
            header: 'Lost reason',
            // Only set for LOST leads; blank otherwise.
            cell: (row) =>
              row.lostReason ? (
                <span className="text-xs text-zinc-700" title={row.lostReason.name}>
                  {row.lostReason.name}
                </span>
              ) : (
                <span className="text-xs text-zinc-400">-</span>
              ),
          },
          {
            id: 'lastFollowUpNote',
            header: 'Latest remark',
            // Note from the lead's most recent follow-up that had a remark.
            cell: (row) =>
              row.lastFollowUpNote ? (
                <span
                  className="block max-w-[240px] truncate text-xs text-zinc-700"
                  title={row.lastFollowUpNote}
                >
                  {row.lastFollowUpNote}
                </span>
              ) : (
                <span className="text-xs text-zinc-400">-</span>
              ),
          },
          {
            id: 'priority',
            header: 'Lead Intent',
            cell: (row) =>
              // Blank (null) intent renders as a dash; every real value shows a
              // badge (NORMAL = "Normal"/medium is now a visible value, distinct
              // from blank).
              row.priority === null || row.priority === undefined ? (
                <span className="text-xs text-zinc-500">-</span>
              ) : (
                <UzBadge tone={PRIORITY_TONE[row.priority]} pill>
                  {row.priority.charAt(0) + row.priority.slice(1).toLowerCase()}
                </UzBadge>
              ),
          },
          {
            // Original owner = the agent the lead was released FROM (former
            // owner). Set on exit-unassigned / reassigned leads; blank when the
            // lead was never reassigned (current owner is also the original).
            id: 'originalOwner',
            header: 'Original owner',
            cell: (row) =>
              row.unassignedFromAgent ? (
                <span className="inline-flex items-center gap-1.5 text-sm text-zinc-700">
                  <UzAvatar name={row.unassignedFromAgent.fullName} size="sm" />
                  <span className="truncate">{row.unassignedFromAgent.fullName}</span>
                </span>
              ) : (
                <span className="text-xs text-zinc-400">-</span>
              ),
          },
          {
            id: 'assignedAgent',
            header: 'Current assigned',
            cell: (row) =>
              row.assignedAgent ? (
                <span className="inline-flex items-center gap-1.5 text-sm text-zinc-700">
                  <UzAvatar name={row.assignedAgent.fullName} size="sm" />
                  <span className="truncate">{row.assignedAgent.fullName}</span>
                </span>
              ) : (
                <span className="text-xs text-zinc-500">Unassigned</span>
              ),
          },
          {
            id: 'budget',
            header: 'Budget',
            cell: (row) => (
              <span className="font-mono text-xs text-zinc-700">
                {formatBudget(row.budgetAmount, row.budgetCurrency)}
              </span>
            ),
          },
          {
            id: 'expectedTravelStart',
            header: 'Travel',
            cell: (row) => (
              <div className="flex flex-col text-xs whitespace-nowrap">
                <span className="text-zinc-700">{formatDate(row.expectedTravelStart)}</span>
                {row.expectedTravelEnd && (
                  <span className="text-zinc-500">→ {formatDate(row.expectedTravelEnd)}</span>
                )}
              </div>
            ),
          },
          {
            id: 'nextFollowUpAt',
            header: 'Next follow-up',
            sortable: true,
            cell: (row) => {
              if (!row.nextFollowUpAt) return <span className="text-xs text-zinc-400">-</span>;
              const d = new Date(row.nextFollowUpAt);
              const overdue = d.getTime() < Date.now();
              return (
                <span
                  className={
                    overdue
                      ? 'text-xs font-medium whitespace-nowrap text-red-600'
                      : 'text-xs whitespace-nowrap text-zinc-700'
                  }
                >
                  {d.toLocaleString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              );
            },
          },
        ];
        // 2026-06-26: append the 6 ad-attribution columns at the
        // END of the grid for ADMIN only. Other roles never see
        // them. Built as plain copies of the row data with a small
        // "-" fallback for null cells so the grid lines up
        // visually. IDs render in mono so the long Meta-style
        // digits don't blow up row height.
        const adCols: ReadonlyArray<DataGridColumn<LeadListItem>> = isAdmin
          ? [
              {
                id: 'adId',
                header: 'Ad ID',
                cell: (row) =>
                  row.adId ? (
                    <span className="font-mono text-xs text-zinc-700">{row.adId}</span>
                  ) : (
                    <span className="text-zinc-400">-</span>
                  ),
              },
              {
                id: 'adName',
                header: 'Ad name',
                cell: (row) =>
                  row.adName ? (
                    <span className="truncate text-zinc-700" title={row.adName}>
                      {row.adName}
                    </span>
                  ) : (
                    <span className="text-zinc-400">-</span>
                  ),
              },
              {
                id: 'adsetId',
                header: 'Adset ID',
                cell: (row) =>
                  row.adsetId ? (
                    <span className="font-mono text-xs text-zinc-700">{row.adsetId}</span>
                  ) : (
                    <span className="text-zinc-400">-</span>
                  ),
              },
              {
                id: 'adsetName',
                header: 'Adset name',
                cell: (row) =>
                  row.adsetName ? (
                    <span className="truncate text-zinc-700" title={row.adsetName}>
                      {row.adsetName}
                    </span>
                  ) : (
                    <span className="text-zinc-400">-</span>
                  ),
              },
              {
                id: 'campaignId',
                header: 'Campaign ID',
                cell: (row) =>
                  row.campaignId ? (
                    <span className="font-mono text-xs text-zinc-700">{row.campaignId}</span>
                  ) : (
                    <span className="text-zinc-400">-</span>
                  ),
              },
              {
                id: 'campaignName',
                header: 'Campaign name',
                cell: (row) =>
                  row.campaignName ? (
                    <span className="truncate text-zinc-700" title={row.campaignName}>
                      {row.campaignName}
                    </span>
                  ) : (
                    <span className="text-zinc-400">-</span>
                  ),
              },
            ]
          : [];
        return [...cols, ...adCols];
      },
    [isAdmin],
  );
};
